// Libs
import classNames from 'classnames/bind';
import { useParams } from 'react-router-dom';
import { Box, Grid2 } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
// Components, Layouts, Pages
import { BaseInput, BaseInputAddress, BaseLabel, BaseSelect } from '~/components/common';
// Others
import { IFormOrder } from '~/utils/interface/order';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IMasterData, IQueryMasterData } from '~/utils/interface/masterData';
import { OrderPickupEnum, TypeMasterDataListEnum } from '~/utils/enum/common';
import { IBaseOption, IPayloadCallApiFn, IResultCallApiFn } from '~/utils/interface/common';
import {
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  optionPaymentTerms,
  optionsDeliveryOrPickUp,
  optionsPricing,
  optionsReferralSource,
  optionsRepeatBusiness,
} from '~/utils/constants/common';
import { getListUser } from '~/thunks/user/userThunk';
import { getFullName } from '~/utils/helper/common';
import { IQueryParamUser } from '~/utils/interface/user';
import { getListMasterData } from '~/thunks/masterData/masterDataThunk';
// Styles, images, icons
import styles from './EventDetailsOrderForm.module.scss';
import { icons } from '~/assets';

type Props = {
  managedByNameDefault?: string;
  salesPersonNameDefault?: string;
};

const cx = classNames.bind(styles);

const EventDetailsOrderForm = (props: Props) => {
  //#region Destructuring Props
  const { managedByNameDefault, salesPersonNameDefault } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const dispatch = useAppDispatch();
  const { id: orderId } = useParams();

  const {
    control,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<IFormOrder>();

  const { eventTitle, deliveryOrPickUp } = watch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const fetchOptionsEventType = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;
      const payloadParams: IQueryMasterData = {
        page: page,
        limit: DEFAULT_LIMIT_PAGE,
        type: TypeMasterDataListEnum.EVENT,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListMasterData(payloadParams)).unwrap();

        const { responses: listEventType, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = handleConvertOptionEventType(listEventType);

        const hasMore: boolean = pagination?.page < pagination?.totalPages || false;

        return { optionsResp: formatOptions, hasMoreResp: hasMore };
      } catch (error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );

  const handleConvertOptionEventType = (data: IMasterData[]): IBaseOption[] => {
    const options: IBaseOption[] = data.map((item) => ({
      value: item.id,
      label: item.name,
    }));

    return options;
  };

  const fetchOptionsReferralSource = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsReferralSource,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsRepeat = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsRepeatBusiness,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsPricing = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsPricing,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsDeliveryOrPickUp = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionsDeliveryOrPickUp,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionsPaymentTerm = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionPaymentTerms,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const fetchOptionManager = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const fetchOptionSalePerson = useCallback(
    async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
      const { page, searchKey } = payload;

      const payloadParams: IQueryParamUser = {
        limit: DEFAULT_LIMIT_PAGE,
        page: page,
        ...(searchKey && { textSearch: searchKey }),
      };

      try {
        const resp = await dispatch(getListUser(payloadParams)).unwrap();

        const { responses, pagination } = resp?.data;

        const formatOptions: IBaseOption[] = responses.map((item) => ({
          value: item?.id,
          label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
        }));

        const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

        return {
          optionsResp: formatOptions,
          hasMoreResp: hasMoreResp,
        };
      } catch (_error) {
        return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
      }
    },
    []
  );
  //#endregion Handle Function

  return (
    <Box id='eventDetailsOrderFormComponent' className={cx('container')}>
      <BaseLabel
        required
        styleType='title'
        label={t('form_orders_title_event_details')}
        icon={icons.commonIconEventDetails}
      />
      <Grid2 container rowSpacing={'10px'} columnSpacing={'18px'}>
        <Grid2 size={4}>
          <Controller
            control={control}
            name='eventTypeId'
            render={({ field: { onChange, value } }) => (
              <BaseSelect
                isSearch
                gap={8}
                label={t('form_orders_label_event_type')}
                callApiFn={fetchOptionsEventType}
                itemNameDefault={eventTitle}
                value={value}
                onChange={(option) => {
                  onChange(option?.value);
                }}
                errorMessage={errors.eventTypeId?.message}
                type='loadMore'
                required
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name='eventTitle'
            render={({ field: { onChange, value } }) => (
              <BaseInput
                label={t('form_orders_label_event_title')}
                value={value}
                onChange={onChange}
                errorMessage={errors.eventTitle?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name='deliveryOrPickUp'
            render={({ field: { onChange, value } }) => (
              <BaseSelect
                gap={6}
                styleType='contained'
                label={t('form_orders_title_delivery_pick_up')}
                callApiFn={fetchOptionsDeliveryOrPickUp}
                value={value}
                onChange={(option) => {
                  onChange(option?.value);
                }}
                errorMessage={errors.deliveryOrPickUp?.message}
                disabled={!!orderId}
              />
            )}
          />
        </Grid2>

        {deliveryOrPickUp === OrderPickupEnum.DELIVERY_PICKUP && (
          <>
            <Grid2 size={6}>
              <Controller
                control={control}
                name='deliveryAddress'
                render={({ field: { value, onChange } }) => (
                  <BaseInputAddress
                    showIcon
                    placeholder={t('form_orders_placeholder_address')}
                    label={t('form_orders_label_event_address')}
                    value={value?.address}
                    onSelectAddress={onChange}
                    errorMessage={errors.deliveryAddress?.address?.message}
                    required
                  />
                )}
              />
            </Grid2>

            <Grid2 size={6}>
              <Controller
                control={control}
                name='pickupAddress'
                render={({ field: { value, onChange } }) => (
                  <BaseInputAddress
                    showIcon
                    placeholder={t('form_orders_placeholder_address')}
                    label={t('form_orders_label_event_pickup_address')}
                    value={value?.address}
                    onSelectAddress={onChange}
                    errorMessage={errors.pickupAddress?.address?.message}
                    required
                  />
                )}
              />
            </Grid2>
          </>
        )}

        <Grid2 size={3}>
          <Controller
            control={control}
            name='referralSource'
            render={({ field: { onChange, value } }) => (
              <BaseSelect
                gap={8}
                styleType='contained'
                label={t('form_orders_label_referral_source')}
                iconLabel={icons.commonIconReferralSource}
                callApiFn={fetchOptionsReferralSource}
                value={value}
                onChange={(option) => {
                  onChange(option?.value);
                }}
                errorMessage={errors.referralSource?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={3}>
          <Controller
            control={control}
            name='repeatBusiness'
            render={({ field: { onChange, value } }) => (
              <BaseSelect
                gap={8}
                styleType='contained'
                label={t('form_orders_label_repeat')}
                iconLabel={icons.commonIconRepeatBusiness}
                callApiFn={fetchOptionsRepeat}
                value={value}
                onChange={(option) => {
                  onChange(option?.value);
                }}
                errorMessage={errors.repeatBusiness?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={3}>
          <Controller
            control={control}
            name='pricingTotal'
            render={({ field: { onChange, value } }) => (
              <BaseSelect
                gap={8}
                styleType='contained'
                label={t('form_orders_title_pricing')}
                callApiFn={fetchOptionsPricing}
                value={value}
                onChange={(option) => {
                  onChange(option?.value);
                }}
                errorMessage={errors.pricingTotal?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={3}>
          <Controller
            control={control}
            name='headCount'
            render={({ field: { onChange, value } }) => (
              <BaseInput
                type='number'
                step={1}
                min={0}
                label={t('form_quotes_label_head_count', { ns: 'admin/quotes' })}
                value={value}
                onChange={onChange}
                errorMessage={errors.headCount?.message}
              />
            )}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'paymentTerms'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseSelect
                  gap={8}
                  styleType='contained'
                  label={t('form_quotes_title_payment_terms', { ns: 'admin/quotes' })}
                  callApiFn={fetchOptionsPaymentTerm}
                  value={value}
                  onChange={(data) => {
                    onChange(data?.value);
                  }}
                  errorMessage={errors?.paymentTerms?.message}
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'managedBy'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseSelect
                  isSearch
                  gap={8}
                  styleType='contained'
                  label={t('form_orders_title_manager')}
                  callApiFn={fetchOptionManager}
                  value={value}
                  type='loadMore'
                  onChange={(data) => {
                    onChange(data?.value);
                  }}
                  itemNameDefault={managedByNameDefault}
                  errorMessage={errors?.managedBy?.message}
                  required
                />
              );
            }}
          />
        </Grid2>

        <Grid2 size={4}>
          <Controller
            control={control}
            name={'salesAccountId'}
            render={({ field: { onChange, value } }) => {
              return (
                <BaseSelect
                  isSearch
                  gap={8}
                  styleType='contained'
                  label={t('form_orders_title_sale_person')}
                  callApiFn={fetchOptionSalePerson}
                  value={value}
                  type='loadMore'
                  onChange={(data) => {
                    onChange(data?.value);
                  }}
                  itemNameDefault={salesPersonNameDefault}
                  errorMessage={errors?.salesAccountId?.message}
                  required
                />
              );
            }}
          />
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default EventDetailsOrderForm;
